/* eslint-disable no-nested-ternary */
import { useState } from "react";
import styles from "./partnerHeader.module.scss";
import CustomLink from "@Components/CustomLink";
import Image from "@Components/Image";
import config from "config";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import MenuDropdown from "@Components/MenuDropdown";
import Support from "@Components/Header/HeaderDesktop/components/Support";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import HeadPhoneIcon from "@Icons/HeadPhoneIcon";
import BurgerMenuIcon from "@Icons/BurgerMenuIcon";
import Chevron from "@Icons/Chevron";
import LoginIcon from "@Icons/LoginIcon";
import LogoutIcon from "@Icons/LogoutIcon";
import LoginDispatcher from "@Components/Login/actions";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { EVENTS } from "../../constants";
import { AppState } from "@Store/index";
import wrapperHOC from "@Utils/wrapperHOC";
import useIsDesktop from "hooks/useIsDesktop";
import FullScreenMobileMenu from "./FullScreenMobileMenu/FullScreenMobileMenu";
import { logoutRequest } from "@Components/Login/components/helper";
import useCustomWindowLocation from "@Components/CustomWindowLocation";
import useTranslationHook from "hooks/useTranslationHook";

type HeaderProps = {
  transparentBg?: boolean;
  handleCTAClick: () => void;
  isModalHeader?: boolean;
};

const PartnerHeader = ({
  transparentBg = true,
  handleCTAClick,
  isModalHeader = false,
}: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslationHook();

  const { isUserLoggedIn, hasPartnerDashboardAccess } = useSelector(
    ({ appData }: AppState) => ({
      isUserLoggedIn: appData.isUserLoggedIn,
      userData: appData.userData,
      hasPartnerDashboardAccess: appData.hasPartnerDashboardAccess,
    }),
    shallowEqual,
  );

  const isDesktop = useIsDesktop();
  const dispatch = useDispatch();
  const customWindowLocation = useCustomWindowLocation();

  const handleSupportTabClick = () => {
    if (isDesktop) {
      gtagEventEmitter({
        action: "clicked_support",
        category: "partner_header",
      });
    } else {
      setIsMobileMenuOpen(true);
    }
  };

  const handleListWithUsClick = () => {
    handleCTAClick();
  };

  const handleLoginClick = () => {
    gtagEventEmitter({
      action: EVENTS.loginButtonClick,
      category: EVENTS.category,
    });
    if (isUserLoggedIn && hasPartnerDashboardAccess) {
      customWindowLocation({ url: "/connect" });
    } else {
      const loginDispatcher = new LoginDispatcher(dispatch);
      loginDispatcher.showLoginModal({ openingSource: "partner_header" });
    }
  };

  const handleLogoutClick = async () => {
    const { data, error } = await logoutRequest();
    if (data) {
      document.cookie = "preferred_partner=;Path=/;";
      window.location.reload();
    } else {
      console.error(error);
    }
  };

  const SupportTab = () => (
    <div
      className={formatClassNames(
        styles.tabItem,
        !isDesktop && (transparentBg ? styles.outlineTbg : styles.outline),
      )}
      style={{
        color: isModalHeader ? "#000" : transparentBg ? "#fff" : "#000",
        marginRight: isDesktop ? 0 : isModalHeader ? 16 : 0,
      }}
      onClick={() => handleSupportTabClick()}
      role="button"
      aria-label="support"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          handleSupportTabClick();
        }
      }}
    >
      {isDesktop ? (
        <HeadPhoneIcon fill={isModalHeader ? "#000" : transparentBg ? "#fff" : "#000"} />
      ) : (
        <BurgerMenuIcon
          fill={isModalHeader ? "#000" : transparentBg ? "#fff" : "#000"}
          width={24}
          height={24}
        />
      )}
      {isDesktop && t("list.header.support")}
      {isDesktop && <Chevron height="12" width="12" />}
    </div>
  );

  return (
    <nav className={styles.listWithUsNav}>
      <div
        className={formatClassNames(
          styles.formModalHeader,
          !transparentBg && styles.nonTransparentBg,
        )}
      >
        <div className={styles.leftSection}>
          <CustomLink to="/" aria-label="home" className={styles.amberLogoContainer}>
            <Image
              dataTestId="Amber-logo"
              src={`${config.IMAGE_STATIC_URL}/images/amber.svg?auto=format`}
              isNotLazy
              height={24}
              width={100}
              alt="amber-logo"
              onClick={() => {
                gtagEventEmitter({ action: "clicked_amber", category: "" });
              }}
              className={formatClassNames(
                styles.amberLogo,
                !isModalHeader && transparentBg && styles.invertColor,
              )}
            />
          </CustomLink>
        </div>

        <div className={styles.rightSection}>
          {isDesktop ? (
            <>
              {/* ⚠️  NOT IN THE MODAL HEADER. Inside the list-with-us form this bar is
                  chrome for a form, not site navigation: a Support menu there offers a way
                  out of a half-filled form and competes with the close control beside it.
                  The page header still carries it. */}
              {!isModalHeader && (
                <MenuDropdown direction="left" menu={<Support />} target={<SupportTab />} />
              )}
              {!isModalHeader && (
                <>
                  {isUserLoggedIn && hasPartnerDashboardAccess ? (
                    <div
                      className={formatClassNames(
                        styles.tabItem,
                        transparentBg ? styles.outlineTbg : styles.outline,
                      )}
                      role="button"
                      onClick={() => handleLogoutClick()}
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          handleLogoutClick();
                        }
                      }}
                    >
                      {t("list.header.dashboard")}
                    </div>
                  ) : isUserLoggedIn ? (
                    <div
                      className={formatClassNames(
                        styles.tabItem,
                        transparentBg ? styles.outlineTbg : styles.outline,
                      )}
                      role="button"
                      onClick={() => handleLogoutClick()}
                      tabIndex={0}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          handleLogoutClick();
                        }
                      }}
                    >
                      <LogoutIcon fill={transparentBg ? "#fff" : "#3f83f8"} />
                      {t("list.header.logout")}
                    </div>
                  ) : (
                    <>
                      <div
                        className={formatClassNames(
                          styles.tabItem,
                          transparentBg ? styles.outlineTbg : styles.outline,
                        )}
                        role="button"
                        onClick={() => handleLoginClick()}
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleLoginClick();
                          }
                        }}
                      >
                        <LoginIcon fill={transparentBg ? "#fff" : "#3f83f8"} />
                        {t("list.header.login")}
                      </div>
                      <div
                        className={formatClassNames(styles.tabItem, styles.solid)}
                        role="button"
                        onClick={() => handleListWithUsClick()}
                        tabIndex={0}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleListWithUsClick();
                          }
                        }}
                      >
                        {t("list.header.listWithUs")}
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          ) : (
            <>
              {/* On mobile `SupportTab` renders as a BURGER, so in the modal it reads as a nav
                  menu inside a form and sits awkwardly beside the close icon. Dropped for the
                  same reason as the desktop Support menu above. */}
              {!isModalHeader && <SupportTab />}
            </>
          )}
        </div>
      </div>

      <FullScreenMobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onLoginClick={handleLoginClick}
        isUserLoggedIn={isUserLoggedIn}
      />
    </nav>
  );
};

export default wrapperHOC(PartnerHeader, {
  componentName: "PartnerHeader",
  showForChina: true,
});
